import { FilterChipContainer, ToggleFiltersButton, ToggleFiltersToolbar } from '@retail/components';
import {
  AssortmentItemsCalculusViewTable,
  AssortmentItemsTablePermissions,
} from '@retail/my-assortment/components';
import { useDisclosure, useSelectedSearchParamState } from '@shared/hooks';
import { useMemo, useState } from 'react';
import { AssortmentPrices } from '@retail/my-assortment/types';
import { Stack } from '@mui/material';
import { ItemPricesFilters } from './ItemPricesFilters';
import {
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
  useUserBrands,
} from '@retail/app/stores/selected-context';
import {
  AddItemToAssortment,
  MgItemContainer,
  MgItemDialogContainer,
} from '@retail/products/containers';
import { OrgUnitType } from '@retail/mgpris/config-types';
import { TotalItemsLoader } from '@retail/products/components';
import { CalculusCardContainer } from '../CalculusCard';
import {
  useFetchAssortmentPrices,
  useFetchAssortmentPricesForSupplier,
} from '@retail/my-assortment/data-access';
import {
  useIncludeVatMyAssortment,
  useItemPricesCalculusViewFilters,
} from '@retail/my-assortment/context';
import { brandLogo } from '@retail/utils';
import { itemPricesFilters } from '@retail/my-assortment/utils';

interface Props {
  supplierNumber?: number;
  isLoadingSuppliers: boolean;
}

export function AssortmentItemsCalculusView({ supplierNumber, isLoadingSuppliers }: Props) {
  const { assortmentId, appTenant, orgUnitType, selectedOrgUnitAssortment } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();
  const { includeVat, toggleIncludeVat } = useIncludeVatMyAssortment();
  const { getPermittedActions } = usePermissionsForCurrentUser();
  const permittedActions: AssortmentItemsTablePermissions = useMemo(
    () => ({
      assortment: getPermittedActions('ASSORTMENT'),
      purchaseAgreements: getPermittedActions('PRICE_CALCULATION__PURCHASE_CONDITION'),
      freight: getPermittedActions('PRICE_CALCULATION__FREIGHT'),
      markup: getPermittedActions('PRICE_CALCULATION__MARKUP'),
      customerMarkup: getPermittedActions('PRICE_CALCULATION__CUSTOMER_MARKUP'),
      priceSuggestions: getPermittedActions('PRICE_CALCULATION__PRICE_SUGGESTIONS'),
    }),
    [getPermittedActions]
  );
  const showCalculusCard =
    orgUnitType !== OrgUnitType.RETAIL_LOCATION_LIGHT &&
    orgUnitType !== OrgUnitType.RETAIL_LOCATION_LIGHT_PLUS;

  const { filterOptions, selectedFilters, errorFetchingFilters, loadingFilters } =
    useItemPricesCalculusViewFilters({
      assortmentId: assortmentId,
      supplierNumber,
    });

  const showFilters = useDisclosure(true);

  const isSupplierSelected = supplierNumber !== undefined;

  const resolvedAssortmentId = selectedFilters.forApproval
    ? selectedOrgUnitAssortment.draftAssortmentId
    : assortmentId;

  const withAgreementPrices =
    selectedFilters.purchasePrice === 'UNSPECIFIED'
      ? undefined
      : selectedFilters.purchasePrice === 'YES';

  const supplierPrices = useFetchAssortmentPricesForSupplier({
    assortmentId: resolvedAssortmentId,
    supplierNumber: supplierNumber as number,
    productGroupNumbers: selectedFilters.productGroupNumbers,
    discountGroupCodes: selectedFilters.discountGroupCodes,
    inAssortment: permittedActions.assortment.includes('READ')
      ? selectedFilters.inAssortment === 'UNSPECIFIED'
      : false,
    withAgreementPrices,
    query: selectedFilters.searchQuery ?? '',
    suspense: false,
    enabled: isSupplierSelected,
  });

  const assortmentPrices = useFetchAssortmentPrices({
    assortmentId: resolvedAssortmentId,
    productGroupNumbers: selectedFilters.productGroupNumbers,
    discountGroupCodes: selectedFilters.discountGroupCodes,
    withAgreementPrices,
    query: selectedFilters.searchQuery ?? '',
    suspense: false,
    enabled: !isSupplierSelected,
  });

  const { data: assortmentPricesDTO, isFetching } = isSupplierSelected
    ? supplierPrices
    : assortmentPrices;
  const assortmentItems = useMemo(
    () =>
      assortmentPricesDTO?.items.map((dto) =>
        AssortmentPrices.fromDTO(dto, {
          name: selectedBrand.name,
          id: selectedBrand.brandId,
          imgUrl: brandLogo(selectedBrand.brandId).logo,
        })
      ) ?? [],
    [assortmentPricesDTO?.items, selectedBrand.brandId, selectedBrand.name]
  );

  const filteredItems = useMemo(
    () =>
      selectedFilters.forApproval
        ? assortmentItems.filter((item) => item.inDraft)
        : assortmentItems,
    [assortmentItems, selectedFilters.forApproval]
  );

  const [selectedCalculusCardItem, , setSelectedCalculusCardItemId] = useSelectedSearchParamState({
    options: filteredItems,
    searchParamKey: 'supplierItem',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  const [selectedProductInfoItem, , setSelectedProductInfoItemId] = useSelectedSearchParamState({
    options: filteredItems,
    searchParamKey: 'item',
    getOptionId: ({ mgSupplierItemId }) => mgSupplierItemId,
  });

  const [itemToAdd, setItemToAdd] = useState<AssortmentPrices | undefined>(undefined);

  return (
    <Stack flexGrow={1} overflow="hidden">
      <ToggleFiltersToolbar sx={{ mb: 1, justifyContent: 'space-between' }}>
        <FilterChipContainer allowedKeys={itemPricesFilters} filterOptions={filterOptions} />

        <Stack flexDirection="row" gap={1} alignItems="flex-end">
          <TotalItemsLoader
            itemsShowing={filteredItems.length}
            totalItems={assortmentPricesDTO?.totalNumberOfItems ?? 0}
            isFetching={isFetching}
          />

          <ToggleFiltersButton
            showFilters={showFilters.isOpen}
            toggleFilters={showFilters.onToggle}
          />
        </Stack>
      </ToggleFiltersToolbar>

      <Stack flexDirection="row" flexGrow={1} overflow="hidden">
        <AssortmentItemsCalculusViewTable
          items={filteredItems}
          selectedCalculusCardItem={selectedCalculusCardItem}
          includeVat={includeVat}
          viewItemProductCard={setSelectedProductInfoItemId}
          viewItemCalculusCard={showCalculusCard ? setSelectedCalculusCardItemId : undefined}
          onAddToAssortment={
            permittedActions.assortment.includes('UPDATE') && selectedFilters.inAssortment === 'NO'
              ? setItemToAdd
              : undefined
          }
          permissions={permittedActions}
          orgType={orgUnitType}
          loading={isFetching || isLoadingSuppliers}
          externalItemSource={appTenant.externalItemSource}
        />

        {selectedCalculusCardItem && (
          <CalculusCardContainer
            {...selectedCalculusCardItem}
            assortmentId={assortmentId}
            onSupplierItemChange={setSelectedCalculusCardItemId}
          />
        )}

        <ItemPricesFilters
          showFilters={showFilters.isOpen}
          filterOptions={filterOptions}
          errorFetchingFilters={errorFetchingFilters}
          loadingFilters={loadingFilters}
          {...selectedFilters}
          disableNotInAssortment={!supplierNumber}
          includeAssortmentFilter={permittedActions.assortment.includes('READ')}
          includeVat={
            permittedActions.assortment.includes('READ')
              ? { toggle: includeVat, onToggle: toggleIncludeVat }
              : undefined
          }
        />
      </Stack>

      {itemToAdd && (
        <AddItemToAssortment
          open
          mgItemNumber={itemToAdd.mgItemNumber}
          supplierId={itemToAdd.supplier.id}
          onClose={() => setItemToAdd(undefined)}
        />
      )}

      {selectedProductInfoItem && (
        <MgItemDialogContainer
          open
          onClose={() => setSelectedProductInfoItemId(null)}
          itemNumber={selectedProductInfoItem.mgItemNumber}
        >
          {({ mgItem }) => (
            <MgItemContainer mgItem={mgItem} showAddItem={false} hiddenBreadcrumbs />
          )}
        </MgItemDialogContainer>
      )}
    </Stack>
  );
}
